import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Globe, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LANG_LIST, MAX_LANGS, parseLang, langLabel } from "@/lib/languages";

const Row = ({ on, onClick, flag, label, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left text-sm transition-colors ${on ? "bg-[var(--accent)]/15 text-[var(--ink)]" : "text-white/75 hover:bg-white/[0.04]"}`}>
    <span className="w-6 text-base leading-none" style={{ fontFamily: '"Twemoji Country Flags", "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}>{flag}</span>
    <span className="flex-1">{label}</span>
    {on && <span className="w-5 h-5 rounded-full bg-[var(--accent)] text-black flex items-center justify-center"><Check size={12} strokeWidth={3} /></span>}
  </button>
);

export function LanguagePicker({ value, onChange, testId = "language-picker", className = "", single = false, autoLabel = "Auto-detect", title = "Languages you speak", hint }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const picked = parseLang(value);
  const list = useMemo(() => LANG_LIST.filter((l) => l.label.toLowerCase().includes(q.trim().toLowerCase())), [q]);
  const label = picked.length ? langLabel(value) : autoLabel;
  const toggle = (id) => {
    if (single) { onChange(id); setOpen(false); setQ(""); return; }
    if (picked.includes(id)) return onChange(picked.filter((c) => c !== id).join(",") || "auto");
    if (picked.length >= MAX_LANGS) { toast.message(`Pick up to ${MAX_LANGS} languages.`); return; }
    onChange([...picked, id].join(","));
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={`select flex items-center gap-2 min-w-0 max-w-full ${className}`}>
        <Globe size={14} className="text-white/50" /> <span className="truncate">{label}</span> <ChevronsUpDown size={13} className="text-white/40 ml-auto" />
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <DialogContent onOpenAutoFocus={(e) => { if (window.matchMedia?.("(pointer: coarse)").matches) e.preventDefault(); }} className="bg-[#14110E] border-white/10 text-[var(--ink)] max-w-md p-0 gap-0 rounded-3xl overflow-hidden" data-testid={`${testId}-dialog`}>
          <div className="px-5 pt-5 pb-3">
            <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
            <DialogDescription className="text-xs text-white/45 mt-1">{hint || `Pick up to ${MAX_LANGS}. Mulankan keeps each sentence in the language you spoke it in.`}</DialogDescription>
            <div className="mt-4 flex items-center gap-2 rounded-2xl bg-white/[0.04] border border-white/10 px-3">
              <Search size={15} className="text-white/40" />
              <input type="search" inputMode="search" enterKeyHint="search" autoComplete="off" autoCapitalize="none" aria-label="Search languages" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 100+ languages" data-testid={`${testId}-search`}
                className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-white/30" />
            </div>
          </div>
          <div className="max-h-[55vh] overflow-y-auto px-2 pb-2 border-t border-white/[0.06] pt-2" data-testid={`${testId}-list`}>
            {!q && <Row on={!picked.length} onClick={() => { onChange("auto"); if (single) setOpen(false); }} flag="🌐" label={autoLabel} testId={`${testId}-option-auto`} />}
            {list.map((l) => <Row key={l.id} on={picked.includes(l.id)} onClick={() => toggle(l.id)} flag={l.flag} label={l.label} testId={`${testId}-option-${l.id}`} />)}
            {!list.length && <p className="text-sm text-white/40 px-4 py-6" data-testid={`${testId}-empty`}>No language matches "{q}".</p>}
          </div>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/[0.06]">
            <span className="text-xs text-white/50 truncate" data-testid={`${testId}-summary`}>{label}</span>
            <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-done`} className="btn-accent text-xs">Done</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
